import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMapPin, FiCalendar, FiHeart, FiShare2, FiUser, FiDollarSign, FiClock, FiTarget, FiCheckCircle } = FiIcons;

const ProjectDetail = () => {
  const { id } = useParams();
  const [selectedDonation, setSelectedDonation] = useState(null);

  // Mock project data (in real app, fetch based on id)
  const project = {
    id: 1,
    title: 'STEM Lab Equipment for Hands-On Learning',
    description: 'Help us create an engaging STEM lab with microscopes, circuit kits, and robotics materials that will transform how our students learn science and technology.',
    fullDescription: `Our 3rd through 5th grade students are eager to explore the world of science and technology, but we lack the hands-on materials that make learning truly engaging. This project will provide essential STEM equipment including digital microscopes, circuit building kits, basic robotics sets, and measurement tools.

    With these materials, students will be able to:
    • Conduct real scientific experiments and observations
    • Build and program simple robots
    • Explore electrical circuits safely
    • Develop critical thinking and problem-solving skills
    • Work collaboratively on engineering challenges

    The impact will be immediate and lasting. Students will develop a deeper understanding of scientific concepts through direct experience, and many will discover a passion for STEM fields that could shape their future careers.`,
    teacher: {
      id: 1,
      name: 'Sarah Johnson',
      school: 'Pine Valley Elementary',
      location: 'Denver, Colorado',
      photo: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=300'
    },
    goal: 2500,
    raised: 1850,
    donorCount: 47,
    daysLeft: 23,
    category: 'Science',
    gradeLevel: '3rd - 5th Grade',
    studentsImpacted: 75,
    createdDate: '2024-02-15',
    images: [
      'https://images.unsplash.com/photo-1581833971358-2c8b550f87b3?w=800',
      'https://images.unsplash.com/photo-1532094349884-543bc11b234d?w=800',
      'https://images.unsplash.com/photo-1507146153580-69a1fe6d8aa1?w=800'
    ],
    materials: [
      { item: 'Digital Microscopes (6 units)', cost: 900 },
      { item: 'Circuit Building Kits (12 sets)', cost: 480 },
      { item: 'Basic Robotics Sets (4 units)', cost: 800 },
      { item: 'Measurement Tools & Supplies', cost: 220 },
      { item: 'Storage & Organization', cost: 100 }
    ],
    recentDonations: [
      { donor: 'Anonymous', amount: 100, date: '2 hours ago', message: 'Happy to support STEM education!' },
      { donor: 'Mike & Lisa K.', amount: 50, date: '1 day ago', message: 'Keep up the great work!' },
      { donor: 'Tech Corp Foundation', amount: 250, date: '2 days ago', message: 'Investing in future innovators.' },
      { donor: 'Jennifer M.', amount: 25, date: '3 days ago', message: 'Every child deserves quality education.' }
    ],
    updates: [
      {
        date: '2024-03-10',
        title: 'Halfway to our goal!',
        content: 'Thanks to amazing supporters like you, we\'re now 74% funded! Students are getting excited about the upcoming STEM activities.'
      },
      {
        date: '2024-03-01',
        title: 'Project launched!',
        content: 'We\'re thrilled to share this project with you. Our students can\'t wait to start exploring science in new ways!'
      }
    ]
  };

  const donationAmounts = [25, 50, 100, 250, 500];

  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const progressPercentage = (project.raised / project.goal) * 100;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Project Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-xl shadow-sm overflow-hidden"
            >
              {/* Image Gallery */}
              <div className="relative">
                <img
                  src={project.images[currentImageIndex]}
                  alt={project.title}
                  className="w-full h-64 md:h-80 object-cover"
                />
                {project.images.length > 1 && (
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                    {project.images.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-2 h-2 rounded-full ${
                          index === currentImageIndex ? 'bg-white' : 'bg-white bg-opacity-50'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="p-6">
                <div className="flex items-center space-x-2 mb-3">
                  <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm">
                    {project.category}
                  </span>
                  <span className="px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-sm">
                    {project.gradeLevel}
                  </span>
                </div>

                <h1 className="text-3xl font-bold text-gray-900 mb-4">{project.title}</h1>
                <p className="text-lg text-gray-600 mb-6">{project.description}</p>

                {/* Teacher Info */}
                <Link
                  to={`/teacher/${project.teacher.id}`}
                  className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <img
                    src={project.teacher.photo}
                    alt={project.teacher.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-gray-900">{project.teacher.name}</p>
                    <p className="text-gray-600">{project.teacher.school}</p>
                    <p className="text-sm text-gray-500 flex items-center">
                      <SafeIcon icon={FiMapPin} className="mr-1" />
                      {project.teacher.location}
                    </p>
                  </div>
                </Link>
              </div>
            </motion.div>

            {/* Project Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Project Description</h2>
              <div className="prose max-w-none text-gray-700">
                {project.fullDescription.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="mb-4 leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>
            </motion.div>

            {/* Materials & Budget */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Materials & Budget</h2>
              <div className="space-y-3">
                {project.materials.map((material, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                    <span className="text-gray-700">{material.item}</span>
                    <span className="font-semibold text-gray-900">${material.cost}</span>
                  </div>
                ))}
                <div className="flex justify-between items-center py-2 font-bold text-lg border-t-2 border-gray-200">
                  <span>Total Project Cost</span>
                  <span className="text-primary-600">${project.goal}</span>
                </div>
              </div>
            </motion.div>

            {/* Project Updates */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Project Updates</h2>
              <div className="space-y-6">
                {project.updates.map((update, index) => (
                  <div key={index} className="border-l-4 border-primary-500 pl-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="font-semibold text-gray-900">{update.title}</h3>
                      <span className="text-sm text-gray-500">{update.date}</span>
                    </div>
                    <p className="text-gray-700">{update.content}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Donation Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-xl shadow-sm p-6 sticky top-8"
            >
              {/* Progress */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-2xl font-bold text-gray-900">${project.raised}</span>
                  <span className="text-gray-600">of ${project.goal}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
                  <div
                    className="bg-primary-500 h-3 rounded-full"
                    style={{ width: `${progressPercentage}%` }}
                  ></div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-lg font-semibold text-gray-900">{project.donorCount}</div>
                    <div className="text-sm text-gray-600">donors</div>
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-gray-900">{project.daysLeft}</div>
                    <div className="text-sm text-gray-600">days left</div>
                  </div>
                </div>
              </div>

              {/* Donation Amounts */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">Choose Amount</h3>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {donationAmounts.map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setSelectedDonation(amount)}
                      className={`p-3 rounded-lg border-2 font-semibold transition-colors ${
                        selectedDonation === amount
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      ${amount}
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  placeholder="Other amount"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  onChange={(e) => setSelectedDonation(parseInt(e.target.value))}
                />
              </div>

              {/* Donate Button */}
              <Link
                to={`/donate/${project.id}`}
                className="block w-full bg-primary-500 text-white text-center py-4 rounded-lg font-semibold hover:bg-primary-600 transition-colors mb-4"
              >
                Donate Now
              </Link>

              {/* Share Buttons */}
              <div className="flex space-x-2">
                <button className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2">
                  <SafeIcon icon={FiShare2} />
                  <span>Share</span>
                </button>
                <button className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2">
                  <SafeIcon icon={FiHeart} />
                  <span>Save</span>
                </button>
              </div>
            </motion.div>

            {/* Project Stats */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <h3 className="font-semibold text-gray-900 mb-4">Project Impact</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiUser} className="text-primary-500" />
                    <span className="text-gray-600">Students Impacted</span>
                  </div>
                  <span className="font-semibold text-gray-900">{project.studentsImpacted}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiCalendar} className="text-primary-500" />
                    <span className="text-gray-600">Created</span>
                  </div>
                  <span className="font-semibold text-gray-900">{project.createdDate}</span>
                </div>
              </div>
            </motion.div>

            {/* Recent Donations */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <h3 className="font-semibold text-gray-900 mb-4">Recent Donations</h3>
              <div className="space-y-4">
                {project.recentDonations.map((donation, index) => (
                  <div key={index} className="border-b border-gray-100 pb-3 last:border-b-0">
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-medium text-gray-900">{donation.donor}</span>
                      <span className="font-semibold text-primary-600">${donation.amount}</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">{donation.message}</p>
                    <p className="text-xs text-gray-500">{donation.date}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;